/**
 * List of racks that have not yet been placed on the layout.
 * Supports bulk selection, auto-placement, single-rack placement, and drag-to-canvas.
 * Racks are grouped by common naming prefix (e.g., A01-A10) for easier management.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Plus, Rows3, Sparkles, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { calculateRackDimensions, findOptimalPosition } from '../utils/positionCalculator';
import { detectRackGroups, RackGroup } from '../utils/rackGrouping';
import { RackSummary } from '../types/warehouseLayout';

interface UnplacedRacksListProps {
  /** Søkefilter fra sidepanelet — matcher mot reolnummer (case-insensitiv) */
  filter?: string;
}

const UnplacedRacksList: React.FC<UnplacedRacksListProps> = ({ filter = '' }) => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, unplacedRacks, showPlacementPanel, selectedUnplacedRacks, rackSummaries } = state;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const handleDragStart = useCallback((e: React.DragEvent, rackNumber: string) => {
    e.dataTransfer.setData('text/plain', rackNumber);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  // Søkefiltrert liste (visning) — bulk-valg opererer fortsatt på hele settet
  const visibleRacks = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return unplacedRacks;
    return unplacedRacks.filter(r => r.rackNumber.toLowerCase().includes(q));
  }, [unplacedRacks, filter]);

  // Group racks by prefix for display
  const rackGroups = useMemo(() => {
    return detectRackGroups(visibleRacks);
  }, [visibleRacks]);

  const toggleGroupCollapse = useCallback((prefix: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(prefix)) {
        next.delete(prefix);
      } else {
        next.add(prefix);
      }
      return next;
    });
  }, []);

  if (!layout || unplacedRacks.length === 0) return null;

  const handleAddRack = (rackNumber: string) => {
    const summary = rackSummaries.find(s => s.rackNumber === rackNumber);
    if (!summary) return;

    // Bruk datadrevne mål (StcBinSize) når de finnes — heuristikk som fallback
    const dimensions = rackDimensionsMap.get(rackNumber) ?? calculateRackDimensions(summary);
    const position = findOptimalPosition(
      layout.racks,
      rackDimensionsMap,
      dimensions,
      layout.dimensions,
      layout.gridUnit
    );

    if (!position) {
      dispatch({ type: 'SET_ERROR', error: t('manager.modules.warehouseLayout.unplacedRacks.noSpaceForRack', {}, 'Kunne ikke finne plass til reol') });
      return;
    }

    dispatch({
      type: 'ADD_RACK',
      rack: { rackNumber, x: position.x, y: position.y, rotation: 0 },
    });
  };

  const handleAddGroupAsRow = (group: RackGroup) => {
    // Place all racks in a group as a linear row using auto-placement
    const newRacks = [...layout.racks];
    let failedCount = 0;

    for (const summary of group.racks) {
      // Datadrevne mål når de finnes — heuristikk kun som fallback
      const dimensions = rackDimensionsMap.get(summary.rackNumber) ?? calculateRackDimensions(summary);
      const currentDimensionsMap = new Map(rackDimensionsMap);
      newRacks.forEach(r => {
        if (currentDimensionsMap.has(r.rackNumber)) return;
        const sum = rackSummaries.find(s => s.rackNumber === r.rackNumber);
        if (sum) currentDimensionsMap.set(r.rackNumber, calculateRackDimensions(sum));
      });

      const position = findOptimalPosition(
        newRacks,
        currentDimensionsMap,
        dimensions,
        layout.dimensions,
        layout.gridUnit
      );

      if (position) {
        newRacks.push({ rackNumber: summary.rackNumber, x: position.x, y: position.y, rotation: 0 });
      } else {
        failedCount++;
      }
    }

    const addedRacks = newRacks.slice(layout.racks.length);
    if (addedRacks.length > 0) {
      dispatch({ type: 'ADD_RACKS', racks: addedRacks });
    }

    if (failedCount > 0) {
      dispatch({ type: 'SET_ERROR', error: t('manager.modules.warehouseLayout.unplacedRacks.someRacksFailed', { '0': failedCount }, `${failedCount} reoler kunne ikke plasseres (for liten plass)`) });
    }
  };

  const renderRackItem = (summary: RackSummary) => {
    const isSelected = selectedUnplacedRacks.includes(summary.rackNumber);
    return (
      <div
        key={summary.rackNumber}
        draggable={!showPlacementPanel}
        onDragStart={!showPlacementPanel ? (e) => handleDragStart(e, summary.rackNumber) : undefined}
        className={`flex items-center justify-between p-1.5 rounded-lg text-sm ${
          showPlacementPanel
            ? isSelected
              ? 'bg-blue-100 border border-blue-400'
              : 'bg-yellow-50 hover:bg-yellow-100 cursor-pointer'
            : 'bg-yellow-50 cursor-grab active:cursor-grabbing hover:bg-yellow-100'
        }`}
        onClick={() => showPlacementPanel && dispatch({ type: 'TOGGLE_UNPLACED_SELECTION', rackNumber: summary.rackNumber })}
      >
        <div className="flex items-center gap-1.5">
          {!showPlacementPanel && (
            <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
          )}
          {showPlacementPanel && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => dispatch({ type: 'TOGGLE_UNPLACED_SELECTION', rackNumber: summary.rackNumber })}
              className="w-3.5 h-3.5 rounded border-gray-300"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <span className="font-medium text-xs">{summary.rackNumber}</span>
          <span className="text-xs text-gray-400">
            {(() => {
              // Reelle mål (StcBinSize-basert) foretrekkes over grid-telling
              const dim = rackDimensionsMap.get(summary.rackNumber);
              if (dim) {
                return `${dim.width.toFixed(1)}m × ${dim.depth.toFixed(1)}m`;
              }
              const vMin = Number.parseInt(summary.minVertical ?? '', 10);
              const vMax = Number.parseInt(summary.maxVertical ?? '', 10);
              const sMin = Number.parseInt(summary.minShelf ?? '', 10);
              const sMax = Number.parseInt(summary.maxShelf ?? '', 10);
              if (!Number.isFinite(vMin) || !Number.isFinite(vMax) || !Number.isFinite(sMin) || !Number.isFinite(sMax)) {
                return `${summary.minVertical ?? '?'}-${summary.maxVertical ?? '?'} × ${summary.minShelf ?? '?'}-${summary.maxShelf ?? '?'}`;
              }
              return `${vMax - vMin + 1}×${sMax - sMin + 1}`;
            })()}
          </span>
        </div>
        {!showPlacementPanel && (
          <button
            onClick={(e) => { e.stopPropagation(); handleAddRack(summary.rackNumber); }}
            className="p-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            title={t('manager.modules.warehouseLayout.unplacedRacks.placeRack', {}, 'Plasser reol')}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  // Determine if we should use grouped display (only when multiple groups exist)
  const useGroupedDisplay = rackGroups.length > 1 || (rackGroups.length === 1 && rackGroups[0].count > 3);

  return (
    <div className="px-3 pb-3">
      {/* Plasseringsverktøy: Smart (maler) og Bulk (manuell serie-plassering) */}
      <div className="flex gap-1.5 mb-2">
        <button
          onClick={() => dispatch({ type: 'OPEN_QUICK_LAYOUT' })}
          className="flex-1 text-xs px-2 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-1 transition-colors"
          title={t('manager.modules.warehouseLayout.unplacedRacks.smartTooltip', {}, 'Smart plassering med maler')}
        >
          <Sparkles className="w-3 h-3" />
          {t('manager.modules.warehouseLayout.unplacedRacks.smart', {}, 'Smart')}
        </button>
        <button
          onClick={() => dispatch({ type: 'TOGGLE_PLACEMENT_PANEL' })}
          className={`flex-1 text-xs px-2 py-1.5 rounded-md flex items-center justify-center gap-1 transition-colors ${
            showPlacementPanel
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          title={t('manager.modules.warehouseLayout.unplacedRacks.bulkTooltip', {}, 'Manuell bulk-plassering')}
        >
          <Rows3 className="w-3 h-3" />
          {t('manager.modules.warehouseLayout.unplacedRacks.bulk', {}, 'Bulk')}
        </button>
      </div>

      {/* Bulk selection controls */}
      {showPlacementPanel && (
        <div className="mb-2 flex gap-2">
          <button
            onClick={() => dispatch({ type: 'SELECT_ALL_UNPLACED' })}
            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
          >
            {t('manager.modules.warehouseLayout.unplacedRacks.selectAll', {}, 'Velg alle')}
          </button>
          <button
            onClick={() => dispatch({ type: 'DESELECT_ALL_UNPLACED' })}
            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
          >
            {t('manager.modules.warehouseLayout.unplacedRacks.deselectAll', {}, 'Fjern valg')}
          </button>
          <span className="text-xs text-gray-500 self-center">
            {t('manager.modules.warehouseLayout.unplacedRacks.selectedCount', { '0': selectedUnplacedRacks.length }, `${selectedUnplacedRacks.length} valgt`)}
          </span>
        </div>
      )}

      {visibleRacks.length === 0 && (
        <p className="text-xs text-gray-400 italic py-1">
          {t('manager.modules.warehouseLayout.sidebar.noSearchHits', { '0': filter.trim() }, `Ingen treff på «${filter.trim()}»`)}
        </p>
      )}

      <div className="space-y-1">
        {useGroupedDisplay ? (
          // Grouped display
          rackGroups.map(group => {
            const isCollapsed = collapsedGroups.has(group.prefix);
            return (
              <div key={group.prefix} className="space-y-0.5">
                {/* Group header */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleGroupCollapse(group.prefix)}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800 py-1"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                    {group.prefix} ({group.count})
                  </button>
                  {!showPlacementPanel && !isCollapsed && (
                    <button
                      onClick={() => handleAddGroupAsRow(group)}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      title={t('manager.modules.warehouseLayout.unplacedRacks.placeAllGroupTooltip', { '0': group.prefix }, `Plasser alle ${group.prefix}-reoler`)}
                    >
                      {t('manager.modules.warehouseLayout.unplacedRacks.placeAll', {}, 'Plasser alle')}
                    </button>
                  )}
                </div>
                {/* Group items */}
                {!isCollapsed && (
                  <div className="space-y-0.5 ml-2">
                    {group.racks.map(renderRackItem)}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          // Flat display (few racks, no meaningful groups)
          visibleRacks.map(renderRackItem)
        )}
      </div>
    </div>
  );
};

export default UnplacedRacksList;
